import { useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'

import { AppBuilderAPI } from '@makyoapp/makyo-sdk'

import { queryKeys } from '@/lib/react-query/query-keys'
import { queryClient } from '@/lib/react-query/react-query'
import { isEmpty } from '@/shared/utils'

/**
 * This hook is used to manage global variable keys in the app builder.
 * for accessing global variable values, please use `window.appGlobalVarsManager` instead.
 */
export const useGlobalVariables = () => {
  const lastWriteRef = useRef<{ vars: string[]; at: number } | null>(null)

  const getLatestVars = async (appID: string) => {
    const queryKey = [queryKeys.appGlobalVariables, appID]
    const cached = queryClient.getQueryData(queryKey) as string[] | undefined
    if (cached) return cached.filter((v) => !isEmpty(v))

    const remote = (await AppBuilderAPI.ReqGetVars(appID)) ?? []
    const cleaned = remote.filter((v) => !isEmpty(v))
    queryClient.setQueryData(queryKey, cleaned)
    return cleaned
  }

  // access global variables from the app builder
  const globalVariablesQuery = useQuery({
    queryKey: [queryKeys.appGlobalVariables, window.appID],
    queryFn: async () => {
      if (!window.appID) return []

      const queryKey = [queryKeys.appGlobalVariables, window.appID]
      const previous =
        (queryClient.getQueryData(queryKey) as string[] | undefined)?.filter((v) => !isEmpty(v)) ?? []

      const remote = (await AppBuilderAPI.ReqGetVars(window.appID)) ?? []
      const cleaned = remote.filter((v) => !isEmpty(v))

      // Safety guard:
      // If the backend transiently returns an empty list, don't let that wipe our cached non-empty list.
      // We only accept an empty list if we *just* saved an empty list intentionally.
      const lastWrite = lastWriteRef.current
      const savedEmptyRecently = !!(
        lastWrite &&
        lastWrite.vars.length === 0 &&
        Date.now() - lastWrite.at < 15_000
      )
      if (cleaned.length === 0 && previous.length > 0 && !savedEmptyRecently) return previous

      return cleaned
    },
    enabled: !!window.appID,
  })

  const globalVariables = useMemo(
    () => globalVariablesQuery.data?.filter((v) => !isEmpty(v)) ?? [],
    [globalVariablesQuery.data]
  )

  const addGlobalVariable = async (varName: string) => {
    if (!window.appID) throw new Error('App ID not set')

    const appID = window.appID
    const queryKey = [queryKeys.appGlobalVariables, appID]
    const previous = (queryClient.getQueryData(queryKey) as string[] | undefined) ?? globalVariables
    const base = await getLatestVars(appID)

    // check if variable already exists
    const isRegistered = base.find((v) => v === varName)
    if (isRegistered) throw new Error('Variable already exists')

    const next = [...base, varName]

    // optimistic update
    queryClient.setQueryData(queryKey, next)

    try {
      // add new variable
      lastWriteRef.current = { vars: next, at: Date.now() }
      await AppBuilderAPI.ReqSaveVars(appID, next)
    } catch (e) {
      // rollback
      queryClient.setQueryData(queryKey, previous)
      throw e
    }
  }

  const removeGlobalVariable = async (varName: string) => {
    if (!window.appID) throw new Error('App ID not set')

    const appID = window.appID
    const queryKey = [queryKeys.appGlobalVariables, appID]
    const previous = (queryClient.getQueryData(queryKey) as string[] | undefined) ?? globalVariables
    const base = await getLatestVars(appID)

    const isRegistered = base.find((v) => v === varName)
    if (!isRegistered) throw new Error('Variable not found')

    const next = base.filter((v) => v !== varName)

    // optimistic update
    queryClient.setQueryData(queryKey, next)

    try {
      // remove variable
      lastWriteRef.current = { vars: next, at: Date.now() }
      await AppBuilderAPI.ReqSaveVars(appID, next)
    } catch (e) {
      // rollback
      queryClient.setQueryData(queryKey, previous)
      throw e
    }
  }

  return {
    globalVariables,
    isLoading: globalVariablesQuery.isLoading,
    addGlobalVariable,
    removeGlobalVariable,
  }
}
